<script>
/**
 * BubbleActions 组件
 *
 * 作用：显示消息的元数据和状态指示器
 *
 * 功能：
 * 1. 显示消息时间戳
 * 2. 显示消息状态图标（根据不同渠道的业务逻辑显示对应的指示器）
 * 3. 显示特殊标记（邮件图标、私密消息锁图标等）
 * 4. 显示社交媒体链接（Twitter、Instagram）
 *
 * 核心概念：
 *
 * 1. messageStatus（消息状态 - 数据层）：
 *    定义：progress、sent、delivered、read、failed
 *    说明：不同渠道可能只使用其中的部分状态
 *    例如：Web Widget 只使用 progress、sent、read
 *
 * 2. showXxxIndicator（指示器方法 - UI层）：
 *    作用：判断是否显示图标，以及显示单勾/双勾
 *    说明：这些方法与消息状态没有直接关系
 *    逻辑：通过渠道类型（如 isAWebWidgetInbox）来判断
 *
 * 3. 渠道业务逻辑决定映射关系：
 *    - 不同渠道有不同的判断规则
 *    - 同一个状态在不同渠道可能触发不同的指示器
 *    - 同一个指示器在不同渠道可能对应不同的状态
 *
 * 指示器图标：
 * ┌──────────────────────┬──────────┬────────────┐
 * │ 指示器方法            │ 图标     │ 图标颜色    │
 * ├──────────────────────┼──────────┼────────────┤
 * │ showSentIndicator    │ ✓ 单勾   │ 紫罗兰色    │
 * │ showDeliveredIndicator│ ✓ 单勾   │ 紫罗兰色    │
 * │ showReadIndicator    │ ✓✓ 双勾  │ 绿色        │
 * │ externalError        │ ⚠ 错误   │ 默认色      │
 * └──────────────────────┴──────────┴────────────┘
 */
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import inboxMixin from 'shared/mixins/inboxMixin';
import { messageTimestamp } from 'shared/helpers/timeHelper';

export default {
  mixins: [inboxMixin],
  props: {
    /** 消息发送者信息 */
    sender: {
      type: Object,
      default: () => ({}),
    },
    /** 消息创建时间（Unix时间戳） */
    createdAt: {
      type: Number,
      default: 0,
    },
    /** Instagram Story 发送者 */
    storySender: {
      type: String,
      default: '',
    },
    /** 外部错误信息（例如：第三方渠道发送失败） */
    externalError: {
      type: String,
      default: '',
    },
    /** Instagram Story ID */
    storyId: {
      type: String,
      default: '',
    },
    /** 是否为邮件消息 */
    isEmail: {
      type: Boolean,
      default: true,
    },
    /** 是否为私密消息（仅客服可见） */
    isPrivate: {
      type: Boolean,
      default: true,
    },
    /** 是否为 Twitter 推文 */
    isATweet: {
      type: Boolean,
      default: true,
    },
    /**
     * 消息类型
     * - MESSAGE_TYPE.INCOMING (0): 客户发送的消息
     * - MESSAGE_TYPE.OUTGOING (1): 客服发送的消息
     * - MESSAGE_TYPE.ACTIVITY (2): 系统活动消息
     * - MESSAGE_TYPE.TEMPLATE (3): 模板消息
     */
    messageType: {
      type: Number,
      default: 1,
    },
    /**
     * 消息状态
     * - 'progress': 发送中
     * - 'sent': 已发送（消息已发送到服务器）
     * - 'delivered': 已送达（消息已送达到客户端）
     * - 'read': 已读（客户已阅读消息）
     * - 'failed': 发送失败
     */
    messageStatus: {
      type: String,
      default: '',
    },
    /**
     * 第三方平台的消息ID
     * - 用于判断消息是否已发送到第三方平台（如WhatsApp、Facebook等）
     * - 有 sourceId 表示消息已被第三方平台接受
     */
    sourceId: {
      type: String,
      default: '',
    },
    /** 收件箱ID（用于获取渠道类型） */
    inboxId: {
      type: [String, Number],
      default: 0,
    },
  },
  computed: {
    /** 获取当前消息所属的收件箱信息 */
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    /** 是否为客户发送的消息（incoming） */
    isIncoming() {
      return MESSAGE_TYPE.INCOMING === this.messageType;
    },
    /** 是否为客服发送的消息（outgoing） */
    isOutgoing() {
      return MESSAGE_TYPE.OUTGOING === this.messageType;
    },
    /** 是否为模板消息 */
    isTemplate() {
      return MESSAGE_TYPE.TEMPLATE === this.messageType;
    },
    /** 判断消息状态是否为 delivered */
    isDelivered() {
      return MESSAGE_STATUS.DELIVERED === this.messageStatus;
    },
    /** 判断消息状态是否为 read */
    isRead() {
      return MESSAGE_STATUS.READ === this.messageStatus;
    },
    /** 判断消息状态是否为 sent */
    isSent() {
      return MESSAGE_STATUS.SENT === this.messageStatus;
    },
    readableTime() {
      return messageTimestamp(this.createdAt, 'LLL d, h:mm a');
    },
    screenName() {
      const { additional_attributes: additionalAttributes = {} } =
        this.sender || {};
      return additionalAttributes?.screen_name || '';
    },
    linkToTweet() {
      if (!this.sourceId || !this.inbox.name) {
        return '';
      }
      const { screenName, sourceId } = this;
      return `https://twitter.com/${
        screenName || this.inbox.name
      }/status/${sourceId}`;
    },
    linkToStory() {
      if (!this.storyId || !this.storySender) {
        return '';
      }
      const { storySender, storyId } = this;
      return `https://www.instagram.com/stories/direct/${storySender}_${storyId}`;
    },
    /**
     * 是否显示状态指示器（单勾/双勾）
     *
     * 显示条件：
     * 1. 是客服发送的消息（outgoing）或模板消息（template）
     * 2. 且不是私密消息（private）
     *
     * 说明：
     * - 客户发送的消息（incoming）不显示状态
     * - 私密消息（仅客服可见）不显示状态
     */
    showStatusIndicators() {
      if ((this.isOutgoing || this.isTemplate) && !this.isPrivate) {
        return true;
      }
      return false;
    },
    /**
     * 是否显示 sent 指示器（单勾图标）
     *
     * 返回 true 的条件：
     *
     * ┌─────────────────────┬──────────────────────────────────┐
     * │ 渠道类型             │ 返回 true 的条件                  │
     * ├─────────────────────┼──────────────────────────────────┤
     * │ Email               │ !!sourceId                       │
     * │ WhatsApp/Twilio     │ sourceId && isSent               │
     * │ Facebook/SMS        │ sourceId && isSent               │
     * │ Telegram            │ sourceId && isSent               │
     * │ Line                │ true                             │
     * │ 其他渠道            │ false                            │
     * └─────────────────────┴──────────────────────────────────┘
     */
    showSentIndicator() {
      if (!this.showStatusIndicators) {
        return false;
      }
      // Email: 只要有 sourceId 就显示 sent
      if (this.isAnEmailChannel) {
        return !!this.sourceId;
      }

      // WhatsApp/Twilio/Facebook/SMS/Telegram: 需要 sourceId + sent 状态
      if (
        this.isAWhatsAppChannel ||
        this.isATwilioChannel ||
        this.isAFacebookInbox ||
        this.isASmsInbox ||
        this.isATelegramChannel
      ) {
        return this.sourceId && this.isSent;
      }

      // Line: 总是显示 sent（因为 Line 没有 sourceId）
      if (this.isALineChannel) {
        return true;
      }

      return false;
    },
    /**
     * 是否显示送达指示器（单勾图标）
     *
     * 返回 true 的条件：
     *
     * ┌─────────────────────┬──────────────────────────────────┐
     * │ 渠道类型             │ 返回 true 的条件                  │
     * ├─────────────────────┼──────────────────────────────────┤
     * │ WhatsApp/Twilio     │ sourceId && isDelivered          │
     * │ Facebook/SMS        │ sourceId && isDelivered          │
     * │ Web Widget          │ isSent                           │
     * │ API Inbox           │ isSent                           │
     * │ Line                │ isDelivered                      │
     * │ 其他渠道            │ false                            │
     * └─────────────────────┴──────────────────────────────────┘
     *
     * 注意：Web Widget 渠道的消息状态只有 progress、sent、read
     * 当状态为 sent 时，通过 isAWebWidgetInbox 判断显示 单勾 指示器
     */
    showDeliveredIndicator() {
      if (!this.showStatusIndicators) {
        return false;
      }

      // WhatsApp/Twilio/Facebook/SMS: 需要 sourceId + delivered 状态
      if (
        this.isAWhatsAppChannel ||
        this.isATwilioChannel ||
        this.isASmsInbox ||
        this.isAFacebookInbox
      ) {
        return this.sourceId && this.isDelivered;
      }

      // Web Widget/API Inbox: sent 状态时显示为 单勾
      if (this.isAWebWidgetInbox || this.isAPIInbox) {
        return this.isSent;
      }

      // Line: 状态为 delivered 时显示
      if (this.isALineChannel) {
        return this.isDelivered;
      }

      return false;
    },
    /**
     * 是否显示 read 指示器（双勾图标）
     *
     * 返回 true 的条件：
     *
     * ┌─────────────────────┬──────────────────────────────────┐
     * │ 渠道类型             │ 返回 true 的条件                  │
     * ├─────────────────────┼──────────────────────────────────┤
     * │ WhatsApp/Twilio     │ sourceId && isRead               │
     * │ Facebook            │ sourceId && isRead               │
     * │ Web Widget          │ isRead                           │
     * │ API Inbox           │ isRead                           │
     * │ 其他渠道            │ false                            │
     * └─────────────────────┴──────────────────────────────────┘
     */
    showReadIndicator() {
      if (!this.showStatusIndicators) {
        return false;
      }

      // WhatsApp/Twilio/Facebook: 需要 sourceId + read 状态
      if (
        this.isAWhatsAppChannel ||
        this.isATwilioChannel ||
        this.isAFacebookInbox
      ) {
        return this.sourceId && this.isRead;
      }

      // Web Widget/API Inbox: 状态为 read 时显示
      if (this.isAWebWidgetInbox || this.isAPIInbox) {
        return this.isRead;
      }

      return false;
    },
  },
};
</script>

<template>
  <div class="message-text--metadata">
    <span
      class="time"
      :class="{
        'has-status-icon':
          showSentIndicator || showDeliveredIndicator || showReadIndicator,
      }"
    >
      {{ readableTime }}
    </span>
    <span v-if="externalError" class="read-indicator-wrap">
      <fluent-icon
        v-tooltip.top-start="externalError"
        icon="error-circle"
        class="action--icon"
        size="14"
      />
    </span>
    <span v-if="showReadIndicator" class="read-indicator-wrap">
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.MESSAGE_READ')"
        icon="checkmark-double"
        class="action--icon read-tick read-indicator"
        size="14"
      />
    </span>
    <span v-else-if="showDeliveredIndicator" class="read-indicator-wrap">
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.DELIVERED')"
        icon="checkmark"
        class="action--icon read-tick"
        size="14"
      />
    </span>
    <span v-else-if="showSentIndicator" class="read-indicator-wrap">
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.SENT')"
        icon="checkmark"
        class="action--icon read-tick"
        size="14"
      />
    </span>
    <fluent-icon
      v-if="isEmail"
      v-tooltip.top-start="$t('CHAT_LIST.RECEIVED_VIA_EMAIL')"
      icon="mail"
      class="action--icon"
      size="16"
    />
    <fluent-icon
      v-if="isPrivate"
      v-tooltip.top-start="$t('CONVERSATION.VISIBLE_TO_AGENTS')"
      icon="lock-closed"
      class="action--icon lock--icon--private"
      size="16"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    />
    <a
      v-if="isATweet && (isOutgoing || isIncoming) && linkToTweet"
      :href="linkToTweet"
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <fluent-icon
        v-tooltip.top-start="$t('CHAT_LIST.VIEW_TWEET_IN_TWITTER')"
        icon="open"
        class="cursor-pointer action--icon"
        size="16"
      />
    </a>
  </div>
</template>

<style lang="scss" scoped>
/**
 * 样式说明：
 * 
 * 图标颜色（客服发送的消息，.right）：
 * - .read-tick：紫罗兰色（violet-100） → 单勾图标
 * - .read-indicator：浅绿色（green-200） → 双勾图标
 */
.right {
  .message-text--metadata {
    @apply items-center;
    .time {
      @apply text-woot-100 dark:text-woot-100;
    }

    .action--icon {
      @apply text-white dark:text-white;

      /** 单勾图标颜色：紫罗兰色 */
      &.read-tick {
        @apply text-violet-100 dark:text-violet-100;
      }

      /** 双勾图标颜色：浅绿色 */
      &.read-indicator {
        @apply text-green-200 dark:text-green-200;
      }
    }

    /** 私密消息锁图标颜色 */
    .lock--icon--private {
      @apply text-slate-400 dark:text-slate-400;
    }
  }
}

.left {
  .message-text--metadata {
    .time {
      @apply text-slate-400 dark:text-slate-200;
    }
  }
}

.message-text--metadata {
  @apply items-start flex;

  .time {
    @apply mr-2 block text-xxs leading-[1.8];
  }

  .action--icon {
    @apply mr-2 ml-2 text-slate-900 dark:text-slate-100;
  }

  a {
    @apply text-slate-900 dark:text-slate-100;
  }
}

.activity-wrap {
  .message-text--metadata {
    .time {
      @apply ml-2 rtl:mr-2 rtl:ml-0 flex text-center text-xxs text-slate-300 dark:text-slate-200;
    }
  }
}

.is-image,
.is-video {
  .message-text--metadata {
    .time {
      @apply bottom-1 text-white dark:text-slate-50 absolute right-2 whitespace-nowrap;

      &.has-status-icon {
        @apply right-8 leading-loose;
      }
    }
    .read-tick {
      @apply absolute bottom-2 right-2;
    }
  }
}

.is-private {
  .message-text--metadata {
    @apply items-center;

    .time {
      @apply text-slate-400 dark:text-slate-400;
    }

    .icon {
      @apply text-slate-400 dark:text-slate-400;
    }
  }

  &.is-image,
  &.is-video {
    .time {
      position: inherit;
      @apply pl-2.5;
    }
  }
}

.delivered-icon {
  @apply ml-4;
}

.read-indicator-wrap {
  @apply leading-none flex items-center;
}
</style>
